import { FC, Ref, useEffect, useState } from 'react';
import { Box, InputAdornment, TextField, TextFieldProps, Typography, useMediaQuery } from '@mui/material';
import { theme } from 'theme';
import { FontWeights } from 'theme/Typography';
import { COLOR_DARK_GREEN, COLOR_GRAY_150, COLOR_GRAY_151, COLOR_WHITE } from 'theme/variables';

export enum InputFieldState {
  ON = 'inputOn',
  OFF = 'inputOff',
  DISABLED = 'disabled',
}

type InputFieldProps = {
  withStartAdornment?: React.CSSProperties;
  style?: React.CSSProperties;
  currentInputState?: InputFieldState;
  externalLabel?: {
    content: string;
    externalOffset: number;
  };
};

export const InputField: FC<InputFieldProps & TextFieldProps> = ({
  withStartAdornment,
  style = {},
  currentInputState = InputFieldState.ON,
  externalLabel,
  ...fieldProps
}) => {
  const [offset, setOffset] = useState<number>();
  const isDownMdLayout = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    function measureTextWidth() {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        context.font = '500 24px "Roboto", sans-serif';
        const { width } = context.measureText(externalLabel?.content ?? '');
        setOffset(width);
      }
    }

    measureTextWidth();
  }, []);

  return (
    <TextField
      label={externalLabel ? externalLabel.content : ''}
      InputLabelProps={{
        style: {
          display: isDownMdLayout ? 'inline' : 'none',
          color: COLOR_DARK_GREEN,
          fontSize: '14px',
          fontFamily: '"Roboto", sans-serif',
          width: '100%',
          textAlign: 'end',
          transform: 'translate(-10px, -20px)',
        },
      }}
      InputProps={{
        startAdornment: withStartAdornment ? (
          <InputAdornment position="start">
            <Typography style={{ ...withStartAdornment }}>$</Typography>
          </InputAdornment>
        ) : (
          ''
        ),
        readOnly: currentInputState !== InputFieldState.ON,
        disabled: currentInputState === InputFieldState.DISABLED,
        defaultValue: 0,
        style,
      }}
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        '&::before': withStartAdornment
          ? {}
          : {
              content: '"$"',
              display: 'inline-block',
              color: COLOR_GRAY_151,
              fontFamily: '"Roboto", sans-serif',
              fontSize: 'clamp(0.875rem, 0.7188rem + 0.6944vw, 1.5rem)',
              lineHeight: '100%',
              fontWeight: FontWeights.fontWeightRegular,
              mr: '5px',
            },
        '&::after':
          externalLabel && !isDownMdLayout && currentInputState === InputFieldState.OFF
            ? {
                content: `"${externalLabel.content}"`,
                position: 'absolute',
                color: COLOR_WHITE,
                fontFamily: '"Roboto", sans-serif',
                fontSize: '24px',
                fontWeight: FontWeights.fontWeightMedium,
                lineHeight: '100%',
                left: `-${offset ? offset + externalLabel.externalOffset + 10 : 0}px`,
              }
            : {},
        '& .MuiInputBase-input': {
          textAlign: 'right',
          height: '1em',
          fontFamily: '"Roboto", sans-serif',
          fontWeight: FontWeights.fontWeightBold,
          cursor: currentInputState !== InputFieldState.ON ? 'default' : 'text',
          padding: 0,
        },
        '.Mui-disabled': {
          WebkitTextFillColor: withStartAdornment ? COLOR_WHITE : COLOR_GRAY_150,
          fontWeight: withStartAdornment ? FontWeights.fontWeightLight : FontWeights.fontWeightBold,
        },
        '&MuiOutlinedInput-input.Mui-disabled': {
          WebkitTextFillColor: COLOR_WHITE,
        },
        '& .MuiOutlinedInput-root': {
          padding: { xs: '10px', md: '20px' },
        },
      }}
      {...fieldProps}
    />
  );
};
